import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import Header from "units/dashboard/components/Header";
import UserSidebar from "./components/UserSidebar";
import { SidebarProvider } from "units/dashboard/context/SidebarProvider";
import Footer from "units/dashboard/components/Footer";
import {
    funnelActivate,
    getSellFunnelStatus,
    getSellFunnelDashboard,
    getSellFunnelCustomers,
    deleteSellFunnel,
} from "./services/sellFunnel";
import { showToast } from "toast/toastHelper";
import FunnelActivation from "./components/funnel/FunnelActivation";
import CustomersModal from "./components/funnel/CustomersModal";
import FunnelDeactivationModal from "./components/funnel/FunnelDeactivationModal";
import {
    Power, TrendingUp, Users, Clock, Sparkles, Layers, ShieldCheck,
    AlertTriangle, Info, BarChart3, Filter, Target, Heart, Award,
    Frown, ShoppingCart, UserPlus,
} from "lucide-react";
import axiosInstance from "api/axiosInstance";
import { useTranslation } from "react-i18next";

const stageIcons = {
    1: "👥",
    2: "❤️",
    3: "🛒",
    4: "💰",
    5: "🏆",
    6: "😡",
};

const stageColors = [
    "#64748B",
    "#3B82F6",
    "#8B5CF6",
    "#10B981",
    "#059669",
    "#EF4444",
];
const useIsDark = () => {
    const get = () =>
        typeof document !== "undefined" &&
        (document.documentElement.classList.contains("dark") ||
            document.body.classList.contains("dark"));

    const [isDark, setIsDark] = useState(get);

    useEffect(() => {
        const update = () => setIsDark(get());
        const obs = new MutationObserver(update);
        obs.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
        obs.observe(document.body, { attributes: true, attributeFilter: ["class"] });
        return () => obs.disconnect();
    }, []);

    return isDark;
};

// [شروع گرادیان، پایان گرادیان] برای هر مرحله
const FUNNEL_PALETTE = {
    light: {
        1: ["#94A3B8", "#64748B"],
        2: ["#38BDF8", "#2563EB"],
        3: ["#A78BFA", "#7C3AED"],
        4: ["#2DD4BF", "#0D9488"],
        5: ["#FCD34D", "#D97706"],
        6: ["#FB7185", "#E11D48"],
    },
    dark: {
        1: ["#475569", "#1E293B"],
        2: ["#0EA5E9", "#1D4ED8"],
        3: ["#8B5CF6", "#5B21B6"],
        4: ["#14B8A6", "#0F766E"],
        5: ["#F59E0B", "#B45309"],
        6: ["#F43F5E", "#9F1239"],
    },
};
const ANALYSIS_STATUS = {
    COMPLETED: "COMPLETED",
    UPDATING: "UPDATING",
    PARTIAL_FAILURE: "PARTIAL_FAILURE",
};

/* ================================================================
 *  Stat Tile
 * ================================================================ */
const StatTile = ({ icon: Icon, label, value, tone = "teal", hint }) => {
    const { i18n } = useTranslation(["user"]);
    const isEnglish = i18n.language === "en";
    const tones = {
        teal: {
            wrap: "border-teal-500/20 dark:border-teal-400/15",
            icon: "from-teal-400/40 to-teal-600/20 border-teal-400/50 text-teal-700 dark:text-teal-300",
            value: "text-teal-700 dark:text-teal-300",
        },
        amber: {
            wrap: "border-amber-500/20 dark:border-amber-400/15",
            icon: "from-amber-300/40 to-amber-500/20 border-amber-300/50 text-amber-700 dark:text-amber-300",
            value: "text-amber-700 dark:text-amber-300",
        },
        slate: {
            wrap: "border-slate-400/20 dark:border-slate-500/15",
            icon: "from-slate-400/40 to-slate-600/20 border-slate-400/50 text-slate-700 dark:text-slate-300",
            value: "text-slate-700 dark:text-slate-300",
        },
    };
    const t = tones[tone];

    return (
        <div
            className={`
                group relative overflow-hidden
                flex items-center gap-3
                rounded-2xl
                bg-white/70 dark:bg-white/5
                backdrop-blur-md
                border ${t.wrap}
                p-3 sm:p-4
                transition-all duration-300
                hover:bg-amber-300/5 dark:hover:bg-amber-400/5
                hover:scale-[1.02]
            `}
        >
            <span
                className={`
                    shrink-0 flex h-11 w-11 items-center justify-center
                    rounded-xl
                    bg-gradient-to-br ${t.icon}
                    border
                    transition-transform duration-300
                    group-hover:scale-110 group-hover:rotate-3
                `}
            >
                <Icon size={18} />
            </span>

            <div className="min-w-0 flex-1">
                <p className="text-[10px] sm:text-[11px] font-bold text-slate-500 dark:text-slate-400 tracking-wide truncate">
                    {label}
                </p>
                <p className={`text-lg sm:text-xl font-extrabold tabular-nums ${isEnglish && 'font-sans'} ${t.value} mt-0.5`}>
                    {value.toLocaleString('en-US')}
                </p>
                {hint && (
                    <p className="text-[10px] text-slate-400 dark:text-slate-500 mt-0.5 truncate">
                        {hint}
                    </p>
                )}
            </div>
        </div>
    );
};

function Qif() {
    const { t, i18n } = useTranslation(["user"]);
    const isEnglish = i18n.language === "en";

    const accessToken = useSelector((state) => state.auth.accessToken);

    const [isFunnelActive, setIsFunnelActive] = useState(false);
    const [funnelData, setFunnelData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dashboardLoading, setDashboardLoading] = useState(false);
    const [analysisStatus, setAnalysisStatus] = useState(null);
    const [statusMessage, setStatusMessage] = useState("");
    const [pendingCount, setPendingCount] = useState(0);
    const [totalCustomers, setTotalCustomers] = useState(0);
    const [selectedItem, setSelectedItem] = useState(null);
    const [customers, setCustomers] = useState([]);
    const [customersLoading, setCustomersLoading] = useState(false);
    const [showCustomersModal, setShowCustomersModal] = useState(false);
    const [activating, setActivating] = useState(false);
    const [showDeactivationModal, setShowDeactivationModal] = useState(false);
    const [deactivating, setDeactivating] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const isDark = useIsDark();
    const pollingRef = useRef(null);

    const clearPolling = () => {
        if (pollingRef.current) {
            clearTimeout(pollingRef.current);
            pollingRef.current = null;
        }
    };

    const loadFunnelDashboard = async ({ silent = false } = {}) => {
        try {
            if (!silent) setDashboardLoading(true);

            const res = await getSellFunnelDashboard(accessToken);
            if (!res?.ok) {
                throw new Error(res?.message || t("funnel.errors.dashboardFailed"));
            }

            const dashboard = res?.data?.data ?? res?.data ?? {};

            if (dashboard.active === false) {
                setIsFunnelActive(false);
                setFunnelData([]);
                setAnalysisStatus(null);
                setStatusMessage("");
                setPendingCount(0);
                setTotalCustomers(0);
                return dashboard;
            }

            setIsFunnelActive(true);
            setAnalysisStatus(dashboard.analysisStatus || null);
            setStatusMessage(dashboard.statusMessage || "");
            setPendingCount(dashboard.pendingCount ?? 0);
            setTotalCustomers(dashboard.totalCustomers ?? 0);

            const stages = Array.isArray(dashboard.stages)
                ? dashboard.stages
                    .slice()
                    .sort((a, b) => a.stageOrder - b.stageOrder)
                    .map((stage, index) => ({
                        id: stage.stageOrder,
                        stageOrder: stage.stageOrder,
                        name: stage.stageName || t("funnel.defaultStageName", { order: stage.stageOrder }),
                        value: Number(stage.count) || 0,
                        fill: stageColors[index] || stageColors[stageColors.length - 1],
                    }))
                : [];

            setFunnelData(stages);
            return dashboard;
        } catch (error) {
            console.error("loadFunnelDashboard error:", error);
            if (!silent) {
                showToast(error?.message || t("funnel.errors.statsFailed"), "error");
            }
            return null;
        } finally {
            if (!silent) setDashboardLoading(false);
        }
    };

    const loadFunnel = async ({ silent = false } = {}) => {
        try {
            if (!silent) setLoading(true);

            const res = await getSellFunnelStatus(accessToken);
            if (!res?.ok) {
                throw new Error(res?.message || t("funnel.errors.statusFailed"));
            }

            const funnel = res?.data?.data ?? res?.data ?? {};

            if (!funnel?.active) {
                setIsFunnelActive(false);
                setFunnelData([]);
                setAnalysisStatus(null);
                setStatusMessage("");
                setPendingCount(0);
                return;
            }

            setIsFunnelActive(true);
            setAnalysisStatus(funnel.analysisStatus || null);
            setStatusMessage(funnel.statusMessage || "");

            const dashboard = await loadFunnelDashboard({ silent });
            return dashboard || funnel;
        } catch (error) {
            console.error("loadFunnel error:", error);
            setIsFunnelActive(false);
            if (!silent) {
                showToast(error?.message || t("funnel.errors.statusFailed"), "error");
            }
            return null;
        } finally {
            if (!silent) setLoading(false);
        }
    };

    const startPolling = () => {
        clearPolling();

        const poll = async () => {
            try {
                const dashboard = await loadFunnel({ silent: true });
                if (!dashboard) return;

                if (dashboard.analysisStatus === ANALYSIS_STATUS.UPDATING) {
                    pollingRef.current = setTimeout(poll, 5000);
                    return;
                }
                clearPolling();
            } catch (error) {
                console.error("Polling error:", error);
                clearPolling();
            }
        };

        poll();
    };

    useEffect(() => {
        if (!accessToken) return;
        loadFunnel();
        return () => {
            clearPolling();
        };
    }, [accessToken]);

    useEffect(() => {
        if (!isFunnelActive || analysisStatus !== ANALYSIS_STATUS.UPDATING) {
            clearPolling();
            return;
        }
        startPolling();
        return () => {
            clearPolling();
        };
    }, [isFunnelActive, analysisStatus]);

    const activateHandler = async () => {
        if (activating) return;
        try {
            setActivating(true);
            const res = await funnelActivate({ templateId: null }, accessToken);
            if (!res?.ok) {
                throw new Error(res?.message || t("funnel.toasts.activateError"));
            }
            showToast(res?.data?.message || t("funnel.toasts.activateSuccess"), "success");
            const funnel = await loadFunnel();
            if (funnel?.analysisStatus === ANALYSIS_STATUS.UPDATING) {
                startPolling();
            }
        } catch (error) {
            console.error("activateHandler error:", error);
            showToast(error?.message || t("funnel.toasts.activateError"), "error");
        } finally {
            setActivating(false);
        }
    };

    const deactivateHandler = async () => {
        if (deactivating) return;
        try {
            setDeactivating(true);
            const res = await deleteSellFunnel(accessToken);
            if (!res?.ok) {
                throw new Error(res?.message || t("funnel.toasts.deactivateError"));
            }
            showToast(res?.data?.message || t("funnel.toasts.deactivateSuccess"), "success");
            setShowDeactivationModal(false);
            setIsFunnelActive(false);
            setFunnelData([]);
            setAnalysisStatus(null);
            setStatusMessage("");
            setPendingCount(0);
            setSelectedItem(null);
            setCustomers([]);
            setShowCustomersModal(false);
            clearPolling();
        } catch (error) {
            console.error("deactivateHandler error:", error);
            showToast(error?.message || t("funnel.toasts.deactivateError"), "error");
        } finally {
            setDeactivating(false);
        }
    };

    const loadStageCustomers = async (stageOrder) => {
        try {
            setCustomersLoading(true);
            setCustomers([]);

            const res = await getSellFunnelCustomers(stageOrder, accessToken);
            if (!res?.ok) {
                throw new Error(res?.message || t("funnel.errors.customersFailed"));
            }

            const data = res?.data?.data ?? res?.data ?? [];
            setCustomers(Array.isArray(data) ? data : []);
            setShowCustomersModal(true);
        } catch (error) {
            console.error("loadStageCustomers error:", error);
            showToast(error?.message || t("funnel.errors.customersFailed"), "error");
        } finally {
            setCustomersLoading(false);
        }
    };

    const handleStageClick = async (stage) => {
        setSelectedItem(stage);
        await loadStageCustomers(stage.stageOrder);
    };

    const handleCustomersModalClose = () => {
        if (customersLoading) return;
        setShowCustomersModal(false);
        setSelectedItem(null);
        setCustomers([]);
    };
    const handleExportFunnelCustomers = async () => {
        if (isExporting) return;

        if (!accessToken) {
            showToast(t("funnel.toasts.notAuthenticated"), "error");
            return;
        }

        try {
            setIsExporting(true);

            const response = await axiosInstance.get(
                "/business/business/funnel/customers/export",
                {
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                    },
                    responseType: "blob",
                }
            );

            const blob = new Blob([response.data], {
                type:
                    response.headers?.["content-type"] ||
                    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;

            const contentDisposition =
                response.headers?.["content-disposition"];
            let fileName = "funnel_customers.xlsx";

            if (contentDisposition) {
                const match = contentDisposition.match(
                    /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
                );
                if (match?.[1]) {
                    fileName = match[1].replace(/['"]/g, "");
                }
            }

            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            showToast(t("funnel.toasts.downloadSuccess"), "success");
        } catch (error) {
            console.error("Export funnel customers error:", error);

            if (error.response?.data instanceof Blob) {
                try {
                    const text = await error.response.data.text();
                    const errorData = JSON.parse(text);
                    showToast(
                        errorData.error ||
                        errorData.message ||
                        t("funnel.toasts.downloadError"),
                        "error"
                    );
                } catch {
                    showToast(t("funnel.toasts.downloadError"), "error");
                }
            } else {
                showToast(t("funnel.toasts.downloadError"), "error");
            }
        } finally {
            setIsExporting(false);
        }
    };

    const total = funnelData.find((stage) => stage.stageOrder === 1)?.value || 0;
    const customerStage = funnelData.find((stage) => stage.stageOrder === 4);
    const loyalStage = funnelData.find((stage) => stage.stageOrder === 5);
    const unhappyStage = funnelData.find((stage) => stage.stageOrder === 6);

    const customerCount = customerStage?.value || 0;
    const loyalCount = loyalStage?.value || 0;
    const unhappyCount = unhappyStage?.value || 0;

    const conversionRate = total > 0 ? ((customerCount / total) * 100).toFixed(1) : "0.0";
    const lostRate = customerCount > 0 ? ((unhappyCount / customerCount) * 100).toFixed(1) : "0.0";
    const retentionRate = customerCount > 0 ? ((loyalCount / customerCount) * 100).toFixed(1) : "0.0";

    return (
        <SidebarProvider>
            {/* ================= Header ================= */}
            <nav className="fixed left-0 right-0 top-0 z-30 flex h-[70px] items-center border-b border-teal-500/15 bg-white/90 backdrop-blur-xl shadow-[0_4px_30px_-12px_rgba(15,118,110,0.3)] dark:border-teal-400/10 dark:bg-slate-900/90 print:hidden">
                <Header />
            </nav>

            {/* ================= Sidebar ================= */}
            <div className="vertical-menu fixed bottom-0 start-0 top-[70px] z-20 h-[calc(100vh-70px)] w-64 overflow-hidden border-r border-teal-500/10 bg-white/90 backdrop-blur-xl dark:border-teal-400/10 dark:bg-slate-900/90 print:hidden">
                <UserSidebar />
            </div>

            {/* ================= Main ================= */}
            <div className="
    min-h-screen min-w-0 overflow-x-hidden
    bg-gradient-to-br from-slate-50 via-white to-teal-50/40
    dark:from-slate-950 dark:via-slate-900 dark:to-slate-950
    pt-[70px]
    lg:ms-64
">
                {/* decorative blobs */}
                <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
                    <div className="absolute -top-32 -right-32 h-96 w-96 rounded-full bg-gradient-to-br from-teal-300/25 to-teal-500/10 blur-3xl dark:from-teal-600/20 dark:to-teal-800/10" />
                    <div className="absolute -bottom-32 -left-32 h-96 w-96 rounded-full bg-gradient-to-br from-amber-300/25 to-amber-500/10 blur-3xl dark:from-amber-600/15 dark:to-amber-800/5" />
                </div>

                <div className="mx-auto w-full max-w-7xl min-w-0 px-3 sm:px-4 md:px-6 py-5 sm:py-6">
                    {/* ============ Page Header ============ */}
                    <div className="mb-5 sm:mb-7">
                        <div className="flex flex-col sm:!flex-row sm:!items-center justify-between gap-4">
                            <div className="flex items-center gap-3 min-w-0">
                                <span className="shrink-0 relative flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-amber-300 via-amber-400 to-amber-600 shadow-[0_8px_24px_-8px_rgba(251,191,36,0.8)] border border-amber-200/60 dark:border-amber-400/30">
                                    <Filter className="h-6 w-6 text-white drop-shadow" />
                                    <Sparkles className="absolute -top-1 -right-1 h-4 w-4 text-amber-100" />
                                </span>
                                <div className="min-w-0">
                                    <h1 className="text-lg sm:text-2xl font-extrabold bg-gradient-to-l from-teal-700 via-teal-600 to-amber-500 dark:from-amber-200 dark:via-teal-300 dark:to-teal-400 bg-clip-text text-transparent">
                                        {t("funnel.pageTitle")}
                                    </h1>
                                    <p className="text-[11px] sm:text-xs text-slate-500 dark:text-slate-400 mt-1">
                                        {t("funnel.pageSubtitle")}
                                    </p>
                                </div>
                            </div>

                            {isFunnelActive && (
                                <div className="flex items-start gap-2">
                                    <button
                                        type="button"
                                        onClick={handleExportFunnelCustomers}
                                        disabled={isExporting}
                                        className="
                                        group shrink-0 self-start sm:self-auto
                                        flex items-center gap-2
                                        rounded-xl px-3.5 py-2.5
                                        text-xs sm:text-sm font-bold
                                        text-teal-600 dark:text-teal-300
                                        bg-teal-500/10 hover:bg-teal-500/20
                                        border border-teal-400/40 hover:border-teal-400/70
                                        backdrop-blur-md
                                        transition-all duration-300
                                        hover:scale-[1.03] active:scale-95
                                        hover:shadow-[0_6px_20px_-6px_rgba(20,184,166,0.6)]
                                        disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100
                                    "
                                    >
                                        {t("funnel.exportData")}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setShowDeactivationModal(true)}
                                        disabled={deactivating}
                                        className="
                                        group shrink-0 self-start sm:self-auto
                                        flex items-center gap-2
                                        rounded-xl px-3.5 py-2.5
                                        text-xs sm:text-sm font-bold
                                        text-rose-600 dark:text-rose-300
                                        bg-rose-500/10 hover:bg-rose-500/20
                                        border border-rose-400/40 hover:border-rose-400/70
                                        backdrop-blur-md
                                        transition-all duration-300
                                        hover:scale-[1.03] active:scale-95
                                        hover:shadow-[0_6px_20px_-6px_rgba(244,63,94,0.6)]
                                        disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100
                                    "
                                    >
                                        <Power size={14} className="group-hover:rotate-12 transition-transform" />
                                        <span>{t("funnel.deactivateFunnel")}</span>
                                    </button>


                                </div>

                            )}
                        </div>
                    </div>

                    {loading ? (
                        <div className="flex min-h-[500px] items-center justify-center">
                            <div className="flex flex-col items-center gap-3">
                                <span className="h-10 w-10 rounded-full border-2 border-teal-500 border-t-transparent animate-spin" />
                                <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">
                                    {t("funnel.loading")}
                                </p>
                            </div>
                        </div>
                    ) : !isFunnelActive ? (
                        <FunnelActivation
                            activating={activating}
                            onActivate={activateHandler}
                        />
                    ) : (
                        <div className="grid grid-cols-1 gap-5 lg:grid-cols-3 lg:gap-6 min-w-0">
                            {/* ===================== Funnel Chart Card ===================== */}
                            <div className="min-w-0 overflow-hidden rounded-2xl bg-white/70 dark:bg-slate-900/70 backdrop-blur-xl border border-teal-500/15 dark:border-teal-400/15 shadow-[0_10px_40px_-20px_rgba(15,118,110,0.4)] dark:shadow-[0_10px_40px_-20px_rgba(0,0,0,0.8)] lg:col-span-2">

                                {/* خط طلایی بالا */}
                                <div className="h-[2px] bg-gradient-to-l from-transparent via-amber-400/80 to-transparent" />

                                {/* ---------- Stats Header ---------- */}
                                <div className="border-b border-teal-500/10 dark:border-teal-400/10 p-4 sm:p-5">
                                    <div className="grid grid-cols-2 gap-2 sm:gap-3">
                                        <StatTile
                                            icon={Clock}
                                            label={t("funnel.stats.pendingAnalysis")}
                                            value={pendingCount.toLocaleString("en-US")}
                                            tone="amber"
                                        />
                                        <StatTile
                                            icon={Users}
                                            label={t("funnel.stats.totalVisitors")}
                                            value={totalCustomers.toLocaleString("en-US")}
                                            tone="teal"
                                        />
                                    </div>

                                    {/* Analysis alert */}
                                    {analysisStatus && analysisStatus !== ANALYSIS_STATUS.COMPLETED && (
                                        <div
                                            className={`
                                                mt-3 flex items-start gap-2.5 rounded-xl px-3.5 py-3 text-xs sm:text-sm font-medium
                                                ${analysisStatus === ANALYSIS_STATUS.PARTIAL_FAILURE
                                                    ? "bg-rose-500/10 border border-rose-400/40 text-rose-700 dark:text-rose-200"
                                                    : "bg-amber-400/15 border border-amber-400/40 text-amber-800 dark:text-amber-200"
                                                }
                                            `}
                                        >
                                            <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                                            <span>
                                                {statusMessage ||
                                                    (analysisStatus === ANALYSIS_STATUS.PARTIAL_FAILURE
                                                        ? t("funnel.analysis.partialFailure")
                                                        : t("funnel.analysis.updating"))}
                                            </span>
                                        </div>
                                    )}
                                </div>

                                {/* ---------- Funnel SVG ---------- */}
                                <div className="w-full px-0 py-3 sm:p-4">
                                    <div className="mx-auto w-full max-w-[520px] rounded-3xl bg-gradient-to-b from-teal-50/70 via-transparent to-amber-50/40 dark:from-white/[0.04] dark:via-transparent dark:to-teal-400/[0.04] p-2 sm:p-3">                                        {dashboardLoading ? (
                                        <div className="flex min-h-[500px] items-center justify-center">
                                            <div className="flex flex-col items-center gap-3">
                                                <span className="h-8 w-8 rounded-full border-2 border-amber-500 border-t-transparent animate-spin" />
                                                <p className="text-xs text-slate-500 dark:text-slate-400">
                                                    {t("funnel.loadingStats")}
                                                </p>
                                            </div>
                                        </div>
                                    ) : funnelData.length === 0 ? (
                                        <div className="flex min-h-[500px] flex-col items-center justify-center gap-3">
                                            <div className="h-14 w-14 rounded-full border-2 border-dashed border-teal-400/40 flex items-center justify-center">
                                                <BarChart3 size={22} className="animate-pulse text-teal-400/70" />
                                            </div>
                                            <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">
                                                {t("funnel.emptyData")}
                                            </p>
                                        </div>
                                    ) : (
                                        <svg
                                            viewBox="0 0 400 480"
                                            className="block h-auto w-full"
                                            preserveAspectRatio="xMidYMid meet"
                                        >
                                            {(() => {
                                                const palette = isDark ? FUNNEL_PALETTE.dark : FUNNEL_PALETTE.light;

                                                const mainStages = funnelData
                                                    .filter((s) => s.stageOrder <= 4)
                                                    .slice()
                                                    .sort((a, b) => a.stageOrder - b.stageOrder);

                                                const loyalStageLocal = funnelData.find((s) => s.stageOrder === 5);
                                                const unhappyStageLocal = funnelData.find((s) => s.stageOrder === 6);
                                                const hasFinalStages = loyalStageLocal || unhappyStageLocal;

                                                const cx = 200;
                                                const stageHeight = 96;
                                                const gap = 6;          // فاصله بین مرحله‌ها
                                                const pad = 3;          // نصف ضخامت stroke برای گوشهٔ گرد
                                                const maxWidth = 400;
                                                const minWidth = 300;
                                                const finalWidth = 260;
                                                const finalHeight = 90;
                                                const half = finalWidth / 2;

                                                const widths =
                                                    mainStages.length <= 1
                                                        ? [maxWidth]
                                                        : mainStages.map((_, i) =>
                                                            maxWidth - (i * (maxWidth - minWidth)) / (mainStages.length - 1)
                                                        );

                                                const firstStageValue = mainStages[0]?.value || 0;
                                                const finalY = mainStages.length * stageHeight;

                                                const opacityOf = (s) => (selectedItem && selectedItem.id !== s.id ? 0.45 : 1);
                                                const filterOf = (s) =>
                                                    selectedItem?.id === s.id ? "url(#funnelSelectedShadow)" : "url(#funnelShadow)";

                                                const funnelElements = mainStages.map((item, index) => {
                                                    const isLast = index === mainStages.length - 1;
                                                    const topW = widths[index] - pad * 2;
                                                    const bottomW =
                                                        (!isLast ? widths[index + 1] : hasFinalStages ? finalWidth : widths[index]) - pad * 2;

                                                    const y0 = index * stageHeight + gap / 2 + pad;
                                                    const y1 = (index + 1) * stageHeight - gap / 2 - pad;
                                                    const centerY = (y0 + y1) / 2;

                                                    const path = `
                M ${cx - topW / 2} ${y0}
                H ${cx + topW / 2}
                L ${cx + bottomW / 2} ${y1}
                H ${cx - bottomW / 2}
                Z
            `;

                                                    const previousValue = mainStages[index - 1]?.value || 0;
                                                    const fromPrevious =
                                                        index === 0 ? 100 : previousValue > 0 ? (item.value / previousValue) * 100 : 0;
                                                    const fromTotal = firstStageValue > 0 ? (item.value / firstStageValue) * 100 : 0;

                                                    return (
                                                        <g
                                                            key={item.id}
                                                            onClick={() => handleStageClick(item)}
                                                            style={{ cursor: "pointer" }}
                                                            opacity={opacityOf(item)}
                                                            className="transition-opacity duration-300"
                                                        >
                                                            <path
                                                                d={path}
                                                                fill={`url(#grad-${item.stageOrder})`}
                                                                stroke={`url(#grad-${item.stageOrder})`}
                                                                strokeWidth={pad * 2}
                                                                strokeLinejoin="round"
                                                                filter={filterOf(item)}
                                                            />
                                                            {/* براق‌سازی */}
                                                            <path
                                                                d={path}
                                                                fill="url(#funnelGloss)"
                                                                stroke="url(#funnelGloss)"
                                                                strokeWidth={pad * 2}
                                                                strokeLinejoin="round"
                                                                pointerEvents="none"
                                                            />

                                                            <g filter="url(#textShadow)" pointerEvents="none">
                                                                <text x={cx} y={centerY - 18} textAnchor="middle" fill="#fff" fontSize="15" fontWeight="700">
                                                                    {stageIcons[item.stageOrder] || "📌"} {item.name}
                                                                </text>
                                                                <text x={cx} y={centerY + 6} textAnchor="middle" fill="#fff" fontSize="22" fontWeight="800" fontFamily={`${isEnglish && 'sans'}`}>
                                                                    {item.value.toLocaleString("en-US")}
                                                                </text>
                                                                <text x={cx} y={centerY + 25} textAnchor="middle" fontSize="11" fontWeight="500" fontFamily={`${isEnglish && 'sans'}`}>
                                                                    {index === 0 ? (
                                                                        <tspan fill="rgba(255,255,255,0.92)">{fromTotal.toFixed(1)}{t("funnel.chart.fromTotal")}</tspan>
                                                                    ) : (
                                                                        <>
                                                                            <tspan fill="#FDE68A" fontWeight="700">{fromTotal.toFixed(1)}{t("funnel.chart.fromTotal")}</tspan>
                                                                            <tspan fill="rgba(255,255,255,0.7)">{" · "}</tspan>
                                                                            <tspan fill="#fff" fontWeight="600">{fromPrevious.toFixed(1)}{t("funnel.chart.fromPrevious")}</tspan>
                                                                        </>
                                                                    )}
                                                                </text>
                                                            </g>
                                                        </g>
                                                    );
                                                });

                                                const renderFinal = (stage, x, w, pctText) => {
                                                    const mx = x + w / 2;
                                                    const y = finalY + gap;
                                                    const h = finalHeight - gap;
                                                    return (
                                                        <g
                                                            onClick={() => handleStageClick(stage)}
                                                            style={{ cursor: "pointer" }}
                                                            opacity={opacityOf(stage)}
                                                            className="transition-opacity duration-300"
                                                        >
                                                            <rect
                                                                x={x} y={y} width={w} height={h} rx="16"
                                                                fill={`url(#grad-${stage.stageOrder})`}
                                                                filter={filterOf(stage)}
                                                            />
                                                            <rect
                                                                x={x} y={y} width={w} height={h} rx="16"
                                                                fill="url(#funnelGloss)"
                                                                pointerEvents="none"
                                                            />
                                                            <g filter="url(#textShadow)" pointerEvents="none">
                                                                <text x={mx} y={y + 24} textAnchor="middle" fill="#fff" fontSize="12" fontWeight="700">
                                                                    {stageIcons[stage.stageOrder] || "📌"} {stage.name}
                                                                </text>
                                                                <text x={mx} y={y + 47} textAnchor="middle" fill="#fff" fontSize="18" fontWeight="800" fontFamily={`${isEnglish && 'sans'}`}>
                                                                    {stage.value.toLocaleString("en-US")}
                                                                </text>
                                                                <text x={mx} y={y + 66} textAnchor="middle" fill="rgba(255,255,255,0.88)" fontSize="10" fontWeight="500" fontFamily={`${isEnglish && 'sans'}`}>
                                                                    {pctText}
                                                                </text>
                                                            </g>
                                                        </g>
                                                    );
                                                };

                                                return (
                                                    <>
                                                        <defs>
                                                            {/* گرادیان هر مرحله */}
                                                            {Object.entries(palette).map(([order, [from, to]]) => (
                                                                <linearGradient key={order} id={`grad-${order}`} x1="0" y1="0" x2="1" y2="1">
                                                                    <stop offset="0%" stopColor={from} />
                                                                    <stop offset="100%" stopColor={to} />
                                                                </linearGradient>
                                                            ))}

                                                            {/* براقی بالای هر مرحله */}
                                                            <linearGradient id="funnelGloss" x1="0" y1="0" x2="0" y2="1">
                                                                <stop offset="0%" stopColor="#fff" stopOpacity={isDark ? 0.18 : 0.32} />
                                                                <stop offset="55%" stopColor="#fff" stopOpacity="0" />
                                                            </linearGradient>

                                                            {/* سایه / درخشش */}
                                                            <filter id="funnelShadow" x="-20%" y="-20%" width="140%" height="150%">
                                                                <feDropShadow
                                                                    dx="0" dy="6" stdDeviation={isDark ? 7 : 5}
                                                                    floodColor={isDark ? "#2DD4BF" : "#0F766E"}
                                                                    floodOpacity={isDark ? 0.18 : 0.22}
                                                                />
                                                            </filter>
                                                            <filter id="funnelSelectedShadow" x="-25%" y="-25%" width="150%" height="160%">
                                                                <feDropShadow
                                                                    dx="0" dy="6" stdDeviation="9"
                                                                    floodColor={isDark ? "#FBBF24" : "#0F766E"}
                                                                    floodOpacity={isDark ? 0.55 : 0.4}
                                                                />
                                                            </filter>
                                                            <filter id="textShadow" x="-10%" y="-10%" width="120%" height="130%">
                                                                <feDropShadow dx="0" dy="1" stdDeviation="1" floodColor="#000" floodOpacity="0.35" />
                                                            </filter>
                                                        </defs>

                                                        {funnelElements}

                                                        {hasFinalStages && (
                                                            <>
                                                                {loyalStageLocal &&
                                                                    renderFinal(loyalStageLocal, cx - half, half - gap / 2, `${retentionRate}${t("funnel.chart.fromCustomers")}`)}
                                                                {unhappyStageLocal &&
                                                                    renderFinal(unhappyStageLocal, cx + gap / 2, half - gap / 2, `${lostRate}${t("funnel.chart.fromCustomers")}`)}
                                                            </>
                                                        )}
                                                    </>
                                                );
                                            })()}
                                        </svg>
                                    )}
                                    </div>
                                </div>
                            </div>

                            {/* ===================== Side Column ===================== */}
                            <div className="min-w-0 space-y-4 sm:space-y-5">
                                {/* ---------- Key Notes Card ---------- */}
                                <div className="relative overflow-hidden rounded-2xl bg-white/70 dark:bg-slate-900/70 backdrop-blur-xl border border-teal-500/15 dark:border-teal-400/15 shadow-[0_10px_40px_-20px_rgba(15,118,110,0.4)] dark:shadow-[0_10px_40px_-20px_rgba(0,0,0,0.8)]">
                                    <div className="h-[2px] bg-gradient-to-l from-transparent via-amber-400/80 to-transparent" />

                                    <div className="p-4 sm:p-5">
                                        <div className="flex items-center gap-2 mb-4">
                                            <span className="shrink-0 flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-amber-300/40 to-teal-400/20 border border-amber-300/40">
                                                <Info size={14} className="text-amber-500" />
                                            </span>
                                            <h3 className="text-sm sm:text-base font-extrabold text-slate-800 dark:text-amber-100">
                                                {t("funnel.keyNotes.title")}
                                            </h3>
                                        </div>

                                        <ul className="space-y-3">
                                            {[
                                                { icon: Target, tone: "text-teal-600 dark:text-teal-300", label: t("funnel.keyNotes.conversionRate.label"), desc: t("funnel.keyNotes.conversionRate.desc") },
                                                { icon: TrendingUp, tone: "text-rose-500", label: t("funnel.keyNotes.churnRate.label"), desc: t("funnel.keyNotes.churnRate.desc") },
                                                { icon: BarChart3, tone: "text-teal-600 dark:text-teal-300", label: t("funnel.keyNotes.percentOfTotal.label"), desc: t("funnel.keyNotes.percentOfTotal.desc") },
                                                { icon: Award, tone: "text-amber-500", label: t("funnel.keyNotes.retentionRate.label"), desc: t("funnel.keyNotes.retentionRate.desc") },
                                            ].map((item, i) => {
                                                const Icon = item.icon;
                                                return (
                                                    <li key={i} className="group flex items-start gap-2.5 rounded-xl px-2 py-1.5 transition-colors hover:bg-amber-300/5 dark:hover:bg-amber-400/5">
                                                        <span className={`shrink-0 mt-0.5 flex h-6 w-6 items-center justify-center rounded-lg bg-white/60 dark:bg-white/5 border border-teal-500/15 dark:border-teal-400/15 ${item.tone} transition-transform duration-300 group-hover:scale-110`}>
                                                            <Icon size={11} />
                                                        </span>
                                                        <div className="min-w-0">
                                                            <p className="text-[11px] sm:text-xs font-extrabold text-slate-700 dark:text-slate-200">
                                                                {item.label}:
                                                            </p>
                                                            <p className="text-[10px] sm:text-[11px] text-slate-500 dark:text-slate-400 leading-5 mt-0.5">
                                                                {item.desc}
                                                            </p>
                                                        </div>
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    </div>
                                </div>

                                {/* ---------- Analysis Status Card ---------- */}
                                {analysisStatus && (
                                    <div className="relative overflow-hidden rounded-2xl bg-white/70 dark:bg-slate-900/70 backdrop-blur-xl border border-teal-500/15 dark:border-teal-400/15 shadow-[0_10px_40px_-20px_rgba(15,118,110,0.4)] dark:shadow-[0_10px_40px_-20px_rgba(0,0,0,0.8)]">
                                        <div className="h-[2px] bg-gradient-to-l from-transparent via-amber-400/80 to-transparent" />

                                        <div className="p-4 sm:p-5">
                                            <div className="flex items-center gap-2 mb-3">
                                                <span className="shrink-0 flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-teal-400/40 to-amber-300/20 border border-teal-400/40">
                                                    <ShieldCheck size={14} className="text-teal-600 dark:text-teal-300" />
                                                </span>
                                                <h3 className="text-sm sm:text-base font-extrabold text-slate-800 dark:text-amber-100">
                                                    {t("funnel.analysis.title")}
                                                </h3>
                                            </div>

                                            <div
                                                className={`
                                                    flex items-start gap-2 rounded-xl px-3 py-2.5 text-[11px] sm:text-xs font-medium leading-6
                                                    ${analysisStatus === ANALYSIS_STATUS.COMPLETED
                                                        ? "bg-emerald-500/10 border border-emerald-400/30 text-emerald-700 dark:text-emerald-300"
                                                        : analysisStatus === ANALYSIS_STATUS.PARTIAL_FAILURE
                                                            ? "bg-rose-500/10 border border-rose-400/30 text-rose-700 dark:text-rose-300"
                                                            : "bg-amber-400/15 border border-amber-400/40 text-amber-800 dark:text-amber-200"
                                                    }
                                                `}
                                            >
                                                <span
                                                    className={`
                                                        shrink-0 mt-1.5 h-1.5 w-1.5 rounded-full
                                                        ${analysisStatus === ANALYSIS_STATUS.COMPLETED
                                                            ? "bg-emerald-500 shadow-[0_0_6px_rgba(16,185,129,0.9)]"
                                                            : analysisStatus === ANALYSIS_STATUS.PARTIAL_FAILURE
                                                                ? "bg-rose-500 shadow-[0_0_6px_rgba(244,63,94,0.9)]"
                                                                : "bg-amber-500 shadow-[0_0_6px_rgba(251,191,36,0.9)]"
                                                        }
                                                    `}
                                                />
                                                <span>
                                                    {statusMessage || t("funnel.analysis.unknown")}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    <div className="mt-8">
                        <Footer />
                    </div>
                </div>
            </div>

            <CustomersModal
                isOpen={showCustomersModal}
                onClose={handleCustomersModalClose}
                stageName={selectedItem?.name}
                customers={customers}
                loading={customersLoading}
            />

            <FunnelDeactivationModal
                isOpen={showDeactivationModal}
                onClose={() => {
                    if (!deactivating) setShowDeactivationModal(false);
                }}
                onConfirm={deactivateHandler}
                loading={deactivating}
            />
        </SidebarProvider>
    );
}

export default Qif;